import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {ElementsTree} from "../../../../model/ElementsTree";
import {Graph, makeNodeFromJs, Node, NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {
  convertJsonSchemaToShorthandIfPossible
} from "../../../json/schema/convert-json-schema-to-shorthand-if-possible";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {ProophBoardDescriptionKeys} from "../../descriptions";
import {getNextGeo} from "../graph-manipulation";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayEventRuntimeInfo} from "./event-definitions";
import {prepareDiffEvent} from "./prepare-diff-event";

export class EventAdded implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'NewEvent';
  public readonly operation: PlayshotChangedOperation = 'added';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.event;
  public readonly diff: PlayshotChangedDiff;

  private newEvent: PlayEventRuntimeInfo;

  constructor(name: string, nodeId: string, event: PlayEventRuntimeInfo, playshot: Playshot) {

    // @TODO: handle aggregate event
    const applyRules = undefined; /* = playshot.playConfig.eventReducers[event.desc.name]; */

    this.name = name;
    this.nodeId = nodeId;
    this.diff = {
      new: prepareDiffEvent(event, applyRules),
      old: null,
    };
    this.newEvent = event;
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = graph.getNode(this.nodeId);

    if(existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the event seems to exist already on the board.`,
        details: `There is at least an element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    const node = makeNodeFromJs({
      id: this.nodeId,
      type: NodeType.event,
      name: nodeLabel(this.name),
      metadata: stringify(omit({
        ...this.newEvent.desc,
        schema: convertJsonSchemaToShorthandIfPossible(this.newEvent.schema),
        // @TODO: handle factory
      }, [
        "name",
        ...ProophBoardDescriptionKeys
      ]))
    })

    graph.addNode(node);
    graph.switchParent(node, syncContext, getNextGeo(addedNodes), true);
    graph.focusNode(node);

    addedNodes[node.getId()] = graph.getNode(node.getId()) || node;

    return makeAsyncIterable({
      cody: `I've added an event card for "${nodeLabel(this.name)}" to the board`
    });
  }
}
